import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock, resolveComponent as _resolveComponent, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-50f48adb"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "el-dialog__title" }
const _hoisted_2 = { class: "datastore-transfer" }
const _hoisted_3 = {
  key: 0,
  class: "datastore-transfer-panel"
}
const _hoisted_4 = { class: "datastore-transfer-head" }
const _hoisted_5 = { class: "datastore-hint" }
const _hoisted_6 = { class: "datastore-transfer-list" }
const _hoisted_7 = { class: "datastore-transfer-name" }
const _hoisted_8 = {
  key: 0,
  class: "datastore-transfer-empty"
}
const _hoisted_9 = {
  key: 0,
  class: "datastore-transfer-pager"
}
const _hoisted_10 = { class: "datastore-transfer-foot" }
const _hoisted_11 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "datastore-transfer-spacer" }, null, -1))
const _hoisted_12 = { class: "datastore-hint" }
const _hoisted_13 = { class: "datastore-transfer-panel" }
const _hoisted_14 = { class: "datastore-transfer-head" }
const _hoisted_15 = { class: "datastore-hint" }
const _hoisted_16 = { class: "datastore-transfer-list" }
const _hoisted_17 = { class: "datastore-transfer-name" }
const _hoisted_18 = {
  key: 0,
  class: "datastore-transfer-empty"
}
const _hoisted_19 = { class: "datastore-transfer-foot" }
const _hoisted_20 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "datastore-transfer-spacer" }, null, -1))
const _hoisted_21 = {
  key: 0,
  id: "datastore-sharing-disabled-hint",
  class: "datastore-hint margin-top-10"
}
const _hoisted_22 = { class: "d-flex justify-content-end align-items-center gap-2" }
const _hoisted_23 = { class: "datastore-hint" }

import { computed, ref, watch } from "vue";
import {
  ElButton,
  ElCheckbox,
  ElDialog,
  ElInput,
  ElOption,
  ElPagination,
  ElScrollbar,
  ElSelect,
  ElTooltip,
} from "element-plus";
import { TYPE } from "vue-toastification";
import { cloneDeep } from "lodash-es";
import { showAlert, showConfirm } from "@/core/popup";
import i18n from "@/core/i18n/i18n";
import {
  DataStoreAccessLevel,
  DataStoreShare,
  DataStoreTable,
} from "@/datastore/types";
import {
  applyShareChanges,
  canDataStore,
  isDataStoreSharingEnabled,
  searchShareTargets,
} from "@/datastore/services/datastore.service";
import {
  availableTargetCount,
  hasShareChanges,
  setAccessFor,
  shareChangeSet,
} from "@/datastore/utils/shareStaging";

const TARGET_PAGE_SIZE = 50;


export default /*#__PURE__*/_defineComponent({
  __name: 'ShareTableDialog',
  props: {
    table: {},
    modelValue: { type: Boolean }
  },
  emits: ["update:modelValue", "changed"],
  setup(__props: any, { emit: __emit }) {

const { t } = i18n.global;

const LEVELS: DataStoreAccessLevel[] = ["READ", "READ_WRITE", "MODIFY"];
const props = __props;
const emit = __emit;

/** The desired end state. Nothing here reaches the server until Save. */
const staged = ref<DataStoreShare[]>([]);
/**
 * The grants the server holds, as far as this dialog knows: the table's own list when the dialog
 * opens, and the staged set once Save has committed it. Held here rather than read off the prop,
 * because the prop is only refreshed by the parent's reload — a round trip the dialog would spend
 * still claiming unsaved changes for, long enough to be asked to discard what it had just saved.
 */
const savedShares = ref<DataStoreShare[]>([]);
const grantedSearch = ref("");

const targets = ref<{ organizationId: string; name: string }[]>([]);
const targetTotal = ref(0);
const targetPage = ref(0);
const targetSize = TARGET_PAGE_SIZE;
const targetSearch = ref("");
/**
 * The term targetTotal was counted under, which the input runs ahead of — through the debounce and
 * again while the request is in flight. Both are set from the same response so they cannot disagree.
 */
const loadedTargetSearch = ref("");

/** Row ticks in each panel — what the arrow buttons act on. */
const leftChecked = ref<string[]>([]);
const rightChecked = ref<string[]>([]);
const grantLevel = ref<DataStoreAccessLevel>("READ");

/** Starts false so the picker is never offered before the parameter has been read. */
const sharingEnabled = ref(false);
let searchTimer: ReturnType<typeof setTimeout> | null = null;
/**
 * Only the newest request may write the picker. Searches are typed faster than they are answered
 * and the dialog is reopened faster still, so without this the reply to an abandoned search — a
 * term from the previous session, or an earlier keystroke of this one — could land last and leave
 * the panel showing results, and a count, belonging to a search no longer being made.
 */
let latestTargetRequest = 0;
const saving = ref(false);

const canEditGrants = computed(() => canDataStore("SHARE"));
const dirty = computed(() => hasShareChanges(savedShares.value, staged.value));

/**
 * An organization already holding a grant lives in the right panel, so it drops out of
 * the left one — the transfer says where each organization is, rather than showing it
 * twice with one copy disabled.
 */
const availableTargets = computed(() =>
  targets.value.filter(
    (target) =>
      !staged.value.some(
        (share) => share.organizationId === target.organizationId
      )
  )
);

/** What the picker still offers, so the header count follows the rows out of the panel. */
const availableTotal = computed(() =>
  availableTargetCount(
    targetTotal.value,
    staged.value,
    loadedTargetSearch.value
  )
);

const visibleStaged = computed(() => {
  const term = grantedSearch.value.trim().toLowerCase();
  if (!term) return staged.value;
  return staged.value.filter((share) =>
    share.name.toLowerCase().includes(term)
  );
});

const allLeftChecked = computed(
  () =>
    availableTargets.value.length > 0 &&
    leftChecked.value.length === availableTargets.value.length
);
/**
 * Over the rows the filter leaves on screen, not over every staged grant: "Select All" ticking
 * organizations the search has hidden would put them one click of the revoke arrow away from being
 * removed without ever having been shown.
 */
const allRightChecked = computed(
  () =>
    visibleStaged.value.length > 0 &&
    visibleStaged.value.every((share) =>
      rightChecked.value.includes(share.organizationId)
    )
);

// The same rule the pager follows on the other panel: a tick means a row the user can see, so
// narrowing or widening the filter drops the ticks rather than carrying hidden ones.
watch(grantedSearch, () => {
  rightChecked.value = [];
});

watch(
  () => [props.modelValue, props.table] as const,
  ([open]) => {
    if (!open) return;
    reset();
  },
  { immediate: true }
);

function reset(): void {
  savedShares.value = cloneDeep(props.table.sharedWith);
  staged.value = cloneDeep(savedShares.value);
  leftChecked.value = [];
  rightChecked.value = [];
  grantedSearch.value = "";
  targetSearch.value = "";
  loadedTargetSearch.value = "";
  targetPage.value = 0;
  // Neither a keystroke nor a reply from the session that just ended may reach the one starting.
  if (searchTimer) clearTimeout(searchTimer);
  latestTargetRequest++;
  // The parameter first: searchShareTargets skips its request when sharing is off, so reading it
  // beforehand keeps that decision from racing.
  isDataStoreSharingEnabled()
    .then(async (enabled) => {
      sharingEnabled.value = enabled;
      await loadTargets();
    })
    .catch(() => {
      // A failed lookup leaves the picker hidden and the list intact — the grants a user came here
      // to revoke must still render.
      sharingEnabled.value = false;
      targets.value = [];
    });
}

async function loadTargets(): Promise<void> {
  const search = targetSearch.value;
  const request = ++latestTargetRequest;
  try {
    const page = await searchShareTargets(search, targetPage.value, targetSize);
    if (request !== latestTargetRequest) return;
    targets.value = page.targets;
    targetTotal.value = page.total;
    loadedTargetSearch.value = search;
  } catch (e: any) {
    if (request !== latestTargetRequest) return;
    targets.value = [];
    targetTotal.value = 0;
    loadedTargetSearch.value = search;
    showAlert(TYPE.ERROR, e.message ?? t("Common.UnknownError"));
  }
}

/** Debounced: the search runs in the database, so a keystroke should not be a query. */
function onTargetSearch(): void {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    targetPage.value = 0;
    loadTargets();
  }, 300);
}

function onTargetPage(page: number): void {
  targetPage.value = page - 1;
  // The ticks refer to rows that are about to be replaced.
  leftChecked.value = [];
  loadTargets();
}

function toggleLeft(organizationId: string): void {
  leftChecked.value = leftChecked.value.includes(organizationId)
    ? leftChecked.value.filter((id) => id !== organizationId)
    : [...leftChecked.value, organizationId];
}

function toggleRight(organizationId: string): void {
  rightChecked.value = rightChecked.value.includes(organizationId)
    ? rightChecked.value.filter((id) => id !== organizationId)
    : [...rightChecked.value, organizationId];
}

function toggleAllLeft(): void {
  leftChecked.value = allLeftChecked.value
    ? []
    : availableTargets.value.map((target) => target.organizationId);
}

function toggleAllRight(): void {
  rightChecked.value = allRightChecked.value
    ? []
    : visibleStaged.value.map((share) => share.organizationId);
}

/** Grants the ticked organizations at the level the picker's "Grant as" is showing. */
function moveRight(): void {
  const added = availableTargets.value
    .filter((target) => leftChecked.value.includes(target.organizationId))
    .map((target) => ({
      organizationId: target.organizationId,
      name: target.name,
      access: grantLevel.value,
    }));
  staged.value = [...staged.value, ...added];
  leftChecked.value = [];
}

/** Revokes the ticked organizations; they reappear in the picker on the left. */
function moveLeft(): void {
  staged.value = staged.value.filter(
    (share) => !rightChecked.value.includes(share.organizationId)
  );
  rightChecked.value = [];
}

function onSetLevel(organizationId: string, level: DataStoreAccessLevel): void {
  staged.value = setAccessFor(staged.value, [organizationId], level);
}

/** Applies one level to every ticked row, leaving the selection in place. */
function onBulkLevel(level: DataStoreAccessLevel): void {
  if (rightChecked.value.length === 0) return;
  staged.value = setAccessFor(staged.value, rightChecked.value, level);
}

async function onSave(): Promise<void> {
  const changes = shareChangeSet(savedShares.value, staged.value);
  // Granting write or structural access is the escalation worth confirming; revoking and
  // narrowing are not, so the confirm is asked for only when something widens.
  const widened = changes.grants.filter((g) => g.access !== "READ");
  if (widened.length > 0) {
    const confirmed = await showConfirm(
      "warning",
      t("DataStore.ConfirmWiderAccess"),
      t("DataStore.ConfirmWiderAccessWarning", { count: widened.length })
    );
    if (!confirmed) return;
  }
  saving.value = true;
  try {
    await applyShareChanges(props.table.id, changes.grants, changes.revoked);
    // Before anything else can consult it: the calls succeeded, so the staged set is now what the
    // server holds, and nothing about this dialog is unsaved any more.
    savedShares.value = cloneDeep(staged.value);
    showAlert(TYPE.SUCCESS, t("DataStore.SharingSaved"));
    emit("changed");
    emit("update:modelValue", false);
  } catch (e: any) {
    showAlert(TYPE.ERROR, e.message ?? t("Common.UnknownError"));
    // The refusal may mean the parameter changed under us; re-read it so the dialog stops
    // offering an action the backend will not perform.
    sharingEnabled.value = await isDataStoreSharingEnabled().catch(() => false);
  } finally {
    saving.value = false;
  }
}

/**
 * @return whether the dialog may close. Discarding reverts the staged set here rather than leaving
 *     it to the next reset, so what the dialog holds always matches what it just told the user it
 *     would keep.
 */
async function confirmDiscard(): Promise<boolean> {
  if (!dirty.value) return true;
  const confirmed = await showConfirm(
    "warning",
    t("DataStore.DiscardShareChanges"),
    t("DataStore.DiscardShareChangesWarning")
  );
  if (confirmed) {
    staged.value = cloneDeep(savedShares.value);
  }
  return confirmed;
}

async function onCancel(): Promise<void> {
  if (!(await confirmDiscard())) return;
  emit("update:modelValue", false);
}

/**
 * The guard for the dialog's own close affordances — the header cross and Escape. It belongs here
 * rather than on the model-value event, which the dialog also emits at the end of every leave
 * transition, however the close was started: asking there put the question a second time after a
 * confirmed cancel, and once more after a save, where the prompt claimed there were changes to
 * discard directly on top of the message saying they had been saved.
 */
async function onBeforeClose(done: () => void): Promise<void> {
  if (await confirmDiscard()) done();
}

/** Plain synchronisation, guarded above: the dialog only ever reports itself closed. */
function onVisibilityChange(value: boolean): void {
  emit("update:modelValue", value);
}

function formatCount(n: number): string {
  return n.toLocaleString("en-US");
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createBlock(_unref(ElDialog), {
    "model-value": _ctx.modelValue,
    width: "900px",
    "custom-class": "datastore-dialog",
    "append-to-body": "",
    "destroy-on-close": "",
    "close-on-click-modal": false,
    "before-close": onBeforeClose,
    "onUpdate:modelValue": onVisibilityChange
  }, {
    title: _withCtx(() => [
      _createElementVNode("span", _hoisted_1, [
        _createTextVNode(_toDisplayString(_unref(t)("DataStore.Share")) + " ", 1),
        _createElementVNode("em", null, _toDisplayString(_ctx.table.name), 1),
        _createTextVNode(" " + _toDisplayString(_unref(t)("DataStore.Table")), 1)
      ])
    ]),
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_22, [
        _createElementVNode("span", _hoisted_23, _toDisplayString(dirty.value ? _unref(t)("DataStore.UnsavedShareChanges") : ""), 1),
        _createVNode(_unref(ElButton), {
          id: "datastore-share-cancel",
          class: "btn-oneTeg3",
          size: "small",
          onClick: onCancel
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)("Common.Cancel")), 1)
          ]),
          _: 1
        }),
        _createVNode(_unref(ElButton), {
          id: "datastore-share-save",
          size: "small",
          class: "btn-oneTeg2",
          loading: saving.value,
          disabled: !dirty.value,
          onClick: onSave
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)("Common.Save")), 1)
          ]),
          _: 1
        }, 8, ["loading", "disabled"])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_2, [
        (sharingEnabled.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("span", null, [
                  _createTextVNode(_toDisplayString(_unref(t)("DataStore.AddOrganizations")) + " ", 1),
                  _createElementVNode("span", _hoisted_5, _toDisplayString(formatCount(availableTotal.value)), 1)
                ]),
                _createVNode(_unref(ElInput), {
                  id: "datastore-share-target-search",
                  modelValue: targetSearch.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((targetSearch).value = $event)),
                  size: "mini",
                  clearable: "",
                  class: "oneTeg-search-input max-w-170",
                  placeholder: _unref(t)('DataStore.SearchOrganizations'),
                  onInput: onTargetSearch
                }, null, 8, ["modelValue", "placeholder"])
              ]),
              _createElementVNode("div", _hoisted_6, [
                _createVNode(_unref(ElScrollbar), { height: "100%" }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableTargets.value, (target) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: target.organizationId,
                        class: _normalizeClass(["mt-1 mb-1 d-flex align-items-center gap-2 transfer-item", {
                'transfer-item-selected': leftChecked.value.includes(
                  target.organizationId
                ),
              }])
                      }, [
                        _createVNode(_unref(ElCheckbox), {
                          id: 'datastore-share-pick-' + target.organizationId,
                          "model-value": leftChecked.value.includes(target.organizationId),
                          onChange: ($event: any) => (toggleLeft(target.organizationId))
                        }, null, 8, ["id", "model-value", "onChange"]),
                        _createElementVNode("span", _hoisted_7, _toDisplayString(target.name), 1)
                      ], 2))
                    }), 128)),
                    (availableTargets.value.length === 0)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(_unref(t)("DataStore.NoOrganizationsFound")), 1))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                })
              ]),
              (targetTotal.value > _unref(targetSize))
                ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                    _createVNode(_unref(ElPagination), {
                      small: "",
                      layout: "prev, pager, next",
                      total: targetTotal.value,
                      "page-size": _unref(targetSize),
                      "current-page": targetPage.value + 1,
                      onCurrentChange: onTargetPage
                    }, null, 8, ["total", "page-size", "current-page"])
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_10, [
                (availableTargets.value.length > 0)
                  ? (_openBlock(), _createBlock(_unref(ElButton), {
                      key: 0,
                      id: "datastore-share-select-all-left",
                      size: "mini",
                      onClick: toggleAllLeft
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(allLeftChecked.value ? _unref(t)("Common.Clear") : _unref(t)("Common.SelectAll")), 1)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                _hoisted_11,
                _createElementVNode("span", _hoisted_12, _toDisplayString(_unref(t)("DataStore.GrantAs")), 1),
                _createVNode(_unref(ElTooltip), {
                  effect: "custom",
                  placement: "top",
                  content: _unref(t)('DataStore.AccessLevelsHint')
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_font_awesome_icon, {
                      icon: "info-circle",
                      class: "datastore-info-icon"
                    })
                  ]),
                  _: 1
                }, 8, ["content"]),
                _createVNode(_unref(ElSelect), {
                  id: "datastore-share-grant-level",
                  modelValue: grantLevel.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((grantLevel).value = $event)),
                  size: "mini",
                  class: "datastore-transfer-level"
                }, {
                  default: _withCtx(() => [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(LEVELS, (level) => {
                      return _createVNode(_unref(ElOption), {
                        key: level,
                        value: level,
                        label: _unref(t)(`DataStore.Access${level}`)
                      }, null, 8, ["value", "label"])
                    }), 64))
                  ]),
                  _: 1
                }, 8, ["modelValue"]),
                _createVNode(_unref(ElTooltip), {
                  effect: "custom",
                  placement: "top",
                  content: _unref(t)('DataStore.Add')
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(ElButton), {
                      id: "datastore-share-add",
                      class: "no-border btn-oneTeg",
                      size: "mini",
                      type: "primary",
                      disabled: leftChecked.value.length === 0,
                      "aria-label": _unref(t)('DataStore.Add'),
                      onClick: moveRight
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_font_awesome_icon, { icon: "arrow-right" })
                      ]),
                      _: 1
                    }, 8, ["disabled", "aria-label"])
                  ]),
                  _: 1
                }, 8, ["content"])
              ])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_13, [
          _createElementVNode("div", _hoisted_14, [
            _createElementVNode("span", null, [
              _createTextVNode(_toDisplayString(_unref(t)("DataStore.OrganizationsWithAccess")) + " ", 1),
              _createElementVNode("span", _hoisted_15, _toDisplayString(staged.value.length), 1)
            ]),
            _createVNode(_unref(ElInput), {
              id: "datastore-share-granted-search",
              modelValue: grantedSearch.value,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((grantedSearch).value = $event)),
              size: "mini",
              clearable: "",
              class: "oneTeg-search-input max-w-170",
              placeholder: _unref(t)('DataStore.SearchGranted')
            }, null, 8, ["modelValue", "placeholder"])
          ]),
          _createElementVNode("div", _hoisted_16, [
            _createVNode(_unref(ElScrollbar), { height: "100%" }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleStaged.value, (share) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: share.organizationId,
                    class: _normalizeClass(["mt-1 mb-1 d-flex align-items-center gap-2 transfer-item", {
                'transfer-item-selected': rightChecked.value.includes(
                  share.organizationId
                ),
              }])
                  }, [
                    _createVNode(_unref(ElCheckbox), {
                      id: 'datastore-share-select-' + share.organizationId,
                      "model-value": rightChecked.value.includes(share.organizationId),
                      onChange: ($event: any) => (toggleRight(share.organizationId))
                    }, null, 8, ["id", "model-value", "onChange"]),
                    _createElementVNode("span", _hoisted_17, _toDisplayString(share.name), 1),
                    _createVNode(_unref(ElSelect), {
                      id: 'datastore-share-level-' + share.organizationId,
                      "model-value": share.access,
                      size: "mini",
                      class: "datastore-transfer-level",
                      disabled: !canEditGrants.value,
                      onChange: 
                  (v) => onSetLevel(share.organizationId, v)
                
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(LEVELS, (level) => {
                          return _createVNode(_unref(ElOption), {
                            key: level,
                            value: level,
                            label: _unref(t)(`DataStore.AccessShort${level}`)
                          }, null, 8, ["value", "label"])
                        }), 64))
                      ]),
                      _: 2
                    }, 1032, ["id", "model-value", "disabled", "onChange"])
                  ], 2))
                }), 128)),
                (visibleStaged.value.length === 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_18, _toDisplayString(_unref(t)("DataStore.NoGrants")), 1))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ]),
          _createElementVNode("div", _hoisted_19, [
            _createVNode(_unref(ElTooltip), {
              effect: "custom",
              placement: "top",
              content: _unref(t)('DataStore.Revoke')
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(ElButton), {
                  id: "datastore-share-remove",
                  class: "no-border btn-oneTeg",
                  size: "mini",
                  type: "primary",
                  disabled: rightChecked.value.length === 0,
                  "aria-label": _unref(t)('DataStore.Revoke'),
                  onClick: moveLeft
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_font_awesome_icon, { icon: "arrow-left" })
                  ]),
                  _: 1
                }, 8, ["disabled", "aria-label"])
              ]),
              _: 1
            }, 8, ["content"]),
            _createVNode(_unref(ElSelect), {
              id: "datastore-share-bulk-level",
              "model-value": '',
              size: "mini",
              class: "datastore-transfer-level",
              placeholder: _unref(t)('DataStore.SetAccessLevel'),
              disabled: !canEditGrants.value || rightChecked.value.length === 0,
              onChange: onBulkLevel
            }, {
              default: _withCtx(() => [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(LEVELS, (level) => {
                  return _createVNode(_unref(ElOption), {
                    key: level,
                    value: level,
                    label: _unref(t)(`DataStore.AccessShort${level}`)
                  }, null, 8, ["value", "label"])
                }), 64))
              ]),
              _: 1
            }, 8, ["placeholder", "disabled"]),
            _hoisted_20,
            (staged.value.length > 0)
              ? (_openBlock(), _createBlock(_unref(ElButton), {
                  key: 0,
                  id: "datastore-share-select-all",
                  size: "mini",
                  onClick: toggleAllRight
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(allRightChecked.value ? _unref(t)("Common.Clear") : _unref(t)("Common.SelectAll")), 1)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ])
        ])
      ]),
      (!sharingEnabled.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_21, _toDisplayString(_unref(t)("DataStore.SharingDisabledHint")), 1))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["model-value"]))
}
}

})